// src/components/commercial/AmbassadorSettings.jsx
//
// Admin page: the numbers behind the ambassador rules (ambassador_setting),
// changed here without a deploy. The server refuses values outside sane
// bounds. Backed by /admin/commercial/ambassador/settings.
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Container, Typography, Box, Button, Paper, Stack, Alert, TextField, CircularProgress, InputAdornment } from '@mui/material';
import useCommercialAdmin from '../../utils/useCommercialAdmin';
import { commercialGet, commercialPut } from '../../utils/commercialApi';

const FIELDS = [
  ['reservation_minutes', 'Durée d’une réservation', 'min'],
  ['max_active_reservations', 'Réservations simultanées par ambassadeur', ''],
  ['reservation_extension_minutes', 'Prolongation', 'min'],
  ['max_reservation_extensions', 'Nombre de prolongations', ''],
  ['reservation_extension_max_m', 'Distance maximale pour prolonger', 'm'],
  ['follow_up_days', 'Durée de la relance réservée à l’ambassadeur', 'jours'],
  ['refusal_cooldown_days', 'Délai avant de revisiter après un refus', 'jours'],
  ['gps_ok_m', 'Check-in valide jusqu’à', 'm'],
  ['gps_warning_m', 'Check-in « à surveiller » jusqu’à (au-delà : à vérifier)', 'm'],
];

export default function AmbassadorSettings() {
  const { ready, forbidden, loading: authLoading, getToken } = useCommercialAdmin();
  const [values, setValues] = useState(null);
  const [bounds, setBounds] = useState({});
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!ready) return;
    commercialGet('/ambassador/settings', getToken())
      .then((d) => { setValues(d.settings); setBounds(d.bounds); })
      .catch((e) => setError(e.message));
  }, [ready, getToken]);

  const save = async () => {
    setError(''); setSaved(false);
    try {
      const d = await commercialPut('/ambassador/settings', getToken(),
        { values: Object.fromEntries(Object.entries(values).map(([k, v]) => [k, Number(v)])) });
      setValues(d.settings);
      setSaved(true);
    } catch (e) {
      setError({ OUT_OF_BOUNDS: 'Une valeur est hors des limites autorisées.', GPS_WARNING_BELOW_OK: 'Le seuil « à surveiller » doit être au-dessus du seuil valide.' }[e.message] ?? e.message);
    }
  };

  if (authLoading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (forbidden) return <Container sx={{ py: 4 }}><Alert severity="error">Accès réservé aux administrateurs.</Alert></Container>;

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4">Règles des ambassadeurs</Typography>
        <Button component={RouterLink} to="/admin/commercial">← Dashboard</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }}>Enregistré. S’applique aux prochaines réservations et visites.</Alert>}
      {!values ? <CircularProgress /> : (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            {FIELDS.map(([name, label, unit]) => (
              <TextField key={name} label={label} type="number" size="small" value={values[name]}
                helperText={bounds[name] ? `entre ${bounds[name][0]} et ${bounds[name][1]}` : ''}
                onChange={(e) => setValues({ ...values, [name]: e.target.value })}
                InputProps={unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined} />
            ))}
            <Button variant="contained" onClick={save}>Enregistrer</Button>
          </Stack>
        </Paper>
      )}
    </Container>
  );
}
